import type { ColumnDef } from "@tanstack/react-table";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import React, { useEffect, useMemo, useState } from "react";

import type { BoardGroup, Item } from "../../types/common";
import { generateItemColumns } from "./colums";
import {
  ChevronToggle,
  Headers,
  Heading,
  Indent,
  Row,
  Scroller,
  StickyTitle,
  SubHeaders,
  SubItems,
  Table,
  VirtualItem,
} from "./table";

interface TableProps {
  items: Item[];
  isRowSelectionEnabled?: boolean;
  depth?: number;
  onDepthChange?: (depth?: number) => void;
  group: BoardGroup;
  showExpander?: boolean;
  onSelectChange?: (update: { selected: string[]; unselected: string[] }) => void;
  selectedItemIds?: { [key: string]: boolean };
  highlightedItemId?: string;
  isClickable?: boolean;
  expandSubitems?: boolean;
  children?: React.ReactNode;
}

export function ItemsTable({
  items = [],
  group,
  isRowSelectionEnabled = true,
  depth,
  onDepthChange,
  onSelectChange,
  selectedItemIds = {},
  showExpander = true,
  highlightedItemId,
  expandSubitems = false,
  children,
}: TableProps) {
  const [expanded, setExpanded] = useState(true);
  const itemTableColumns = useMemo(() => generateItemColumns(), []);
  const [rowSelection, setRowSelection] = React.useState<{ [key: string]: boolean }>(selectedItemIds);
  const cols = React.useMemo<ColumnDef<Item>[]>(
    () =>
      itemTableColumns
        .filter((header) => isRowSelectionEnabled || header.id !== "select")
        .filter((header) => expanded || header.id === "color"),
    [itemTableColumns, isRowSelectionEnabled, expanded]
  );

  const table = useReactTable<Item>({
    data: items,
    columns: cols,
    state: {
      rowSelection,
    },
    meta: {
      depth,
      color: group.color,
    },
    onRowSelectionChange: setRowSelection,
    enableRowSelection: (row) => !Number.isInteger(depth) || depth === row.depth,
    enableSubRowSelection: false,
    getRowCanExpand: (row) => row.subRows.length > 0,
    getCoreRowModel: getCoreRowModel(),
    getSubRows: (row) => row.subitems,
    getRowId: (row) => row.id,
    ...(expandSubitems && {
      initialState: {
        expanded: true,
      },
    }),
  });

  const rows = table.getRowModel().rows;
  const selectedFlatRows = table.getSelectedRowModel().flatRows;
  const flatRows = table.getRowModel().flatRows.map((i) => i.id);
  const keys = Object.keys(rowSelection);
  const unselected = flatRows.filter((id) => !keys.includes(id));
  const tableContainerRef = React.useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    paddingStart: 36,
    count: rows.length,
    getScrollElement: React.useCallback(() => tableContainerRef.current, []),
    estimateSize: React.useCallback(() => 36, []),
    overscan: 5,
  });
  const virtualRows = rowVirtualizer.getVirtualItems();

  useEffect(() => {
    onSelectChange?.({ selected: Object.keys(rowSelection), unselected });
  }, [onSelectChange, JSON.stringify(rowSelection)]);

  useEffect(() => {
    if (selectedFlatRows.length > 0) {
      const [{ depth }] = selectedFlatRows;
      onDepthChange?.(depth);
    }
  }, [onDepthChange, JSON.stringify(rowSelection)]);

  useEffect(() => {
    setRowSelection(selectedItemIds);
  }, [JSON.stringify(selectedItemIds)]);

  function onToggleExpand() {
    setExpanded((prev) => !prev);
  }

  return (
    <Scroller ref={tableContainerRef} style={{ maxHeight: "auto" }}>
      {showExpander && (
        <StickyTitle className={!expanded ? "expanded" : ""}>
          <Heading color={group.color}>
            <ChevronToggle onClick={onToggleExpand} expanded={expanded} /> <h4>{group.title}</h4>
          </Heading>
        </StickyTitle>
      )}
      <Table
        expanded={expanded}
        style={{
          height: expanded ? `${rowVirtualizer.getTotalSize()}px` : "auto",
          width: "100%",
          position: "relative",
        }}
      >
        <Headers table={table} group={group} expanded={expanded} showExpander={showExpander} />
        {expanded &&
          virtualRows.map((virtualRow) => {
            const row = rows[virtualRow.index];
            return (
              <VirtualItem
                key={virtualRow.index}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: `${virtualRow.size}px`,
                  transform: `translateY(${virtualRow.start}px)`,
                }}
              >
                <Row
                  row={row}
                  color={group.color}
                  rowVirtualizer={rowVirtualizer}
                  index={virtualRow.index}
                  isHighlighted={row.id === highlightedItemId}
                  isSelected={row.getIsSelected()}
                  canSelect={row.getCanSelect()}
                  isExpanded={row.getIsExpanded()}
                  isExpandable={row.getCanExpand()}
                >
                  <SubItems>
                    <Indent />
                    <div className="inner">
                      <SubHeaders table={table} group={group} subRows={row.subRows} />
                      {row.subRows.map((subRow) => (
                        <Row
                          key={subRow.id}
                          row={subRow}
                          color={group.color}
                          isHighlighted={row.id === highlightedItemId}
                          isSelected={subRow.getIsSelected()}
                          canSelect={subRow.getCanSelect()}
                          isExpanded={row.getIsExpanded()}
                        />
                      ))}
                    </div>
                  </SubItems>
                </Row>
              </VirtualItem>
            );
          })}
      </Table>
      {children}
    </Scroller>
  );
}
